import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Camera,
  CircleOff,
  Globe,
  MessageCircle,
  Pencil,
  Plus,
  Radio,
  Search,
} from 'lucide-react';
import Modal from '../../components/ui/Modal';
import ConfirmModal from '../../components/ui/ConfirmModal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import EmptyState from '../../components/ui/EmptyState';
import { getApiErrorMessage } from '../../utils/errors';
import { listAgents } from '../../api/agents';
import {
  createChannel,
  deleteChannel,
  getAccountAvailableCatalogs,
  listChannels,
  updateChannel,
} from '../../api/channels';

const CHANNEL_TYPES = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'web', label: 'Web / PWA' },
];

export default function ClientChannels() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialAgentFilter = searchParams.get('agentId') || '';

  const [channels, setChannels] = useState([]);
  const [agents, setAgents] = useState([]);
  const [availableCatalogs, setAvailableCatalogs] = useState({ whatsapp: [], instagram: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [agentFilter, setAgentFilter] = useState(initialAgentFilter);

  // Create Modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createForm, setCreateForm] = useState({
    agentId: initialAgentFilter,
    name: '',
    type: 'whatsapp',
    catalogId: '',
    deliveryUrl: '',
    deliveryToken: '',
  });

  // Edit Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editingChannel, setEditingChannel] = useState(null);
  const [editName, setEditName] = useState('');

  // Deactivate Modal
  const [deactivatingChannel, setDeactivatingChannel] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [channelsRes, agentsRes] = await Promise.all([
        listChannels({ agentId: agentFilter || undefined }),
        listAgents({ perPage: 100 }),
      ]);
      setChannels(channelsRes.data || []);
      setAgents(agentsRes.data || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os canais.'));
    } finally {
      setLoading(false);
    }
  }, [agentFilter]);

  const loadCatalogs = useCallback(async () => {
    try {
      const catalogs = await getAccountAvailableCatalogs();
      setAvailableCatalogs(catalogs || { whatsapp: [], instagram: [] });
    } catch (err) {
      // silently handle or log
    }
  }, []);

  useEffect(() => {
    loadData();
    loadCatalogs();
  }, [loadData, loadCatalogs]);

  const handleOpenCreate = () => {
    setCreateForm({
      agentId: agentFilter || (agents[0]?.id ? String(agents[0].id) : ''),
      name: '',
      type: 'whatsapp',
      catalogId: availableCatalogs.whatsapp[0]?.id ? String(availableCatalogs.whatsapp[0].id) : '',
      deliveryUrl: '',
      deliveryToken: '',
    });
    setCreateError('');
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!createForm.agentId) {
      setCreateError('Selecione um agente para vincular o canal.');
      return;
    }
    if (!createForm.name.trim()) {
      setCreateError('Informe um nome para o canal.');
      return;
    }
    if (createForm.type === 'whatsapp' && !createForm.catalogId) {
      setCreateError('Selecione um catálogo/número de WhatsApp disponível.');
      return;
    }
    if (createForm.type === 'instagram' && !createForm.catalogId) {
      setCreateError('Selecione uma conta de Instagram disponível.');
      return;
    }

    setCreateSaving(true);
    setCreateError('');

    try {
      await createChannel({
        agentId: createForm.agentId,
        name: createForm.name.trim(),
        type: createForm.type,
        catalogId: createForm.catalogId || undefined,
        deliveryUrl: createForm.deliveryUrl || undefined,
        deliveryToken: createForm.deliveryToken || undefined,
      });
      setSuccess('Canal conectado com sucesso ao agente!');
      setCreateModalOpen(false);
      loadData();
    } catch (err) {
      setCreateError(getApiErrorMessage(err, 'Erro ao conectar canal.'));
    } finally {
      setCreateSaving(false);
    }
  };

  const handleOpenEdit = (channel) => {
    setEditingChannel(channel);
    setEditName(channel.name);
    setEditError('');
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editName.trim()) {
      setEditError('Nome do canal é obrigatório.');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      await updateChannel(editingChannel.id, { name: editName.trim() });
      setSuccess('Canal atualizado com sucesso!');
      setEditModalOpen(false);
      loadData();
    } catch (err) {
      setEditError(getApiErrorMessage(err, 'Erro ao atualizar canal.'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivatingChannel) return;
    setDeactivating(true);
    try {
      await deleteChannel(deactivatingChannel.id);
      setSuccess('Canal desativado com sucesso.');
      setDeactivatingChannel(null);
      loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível desativar o canal.'));
    } finally {
      setDeactivating(false);
    }
  };

  const filteredChannels = channels.filter((ch) => {
    if (search && !ch.name.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    return true;
  });

  const agentOptions = [
    { value: '', label: 'Todos os agentes' },
    ...agents.map((a) => ({ value: String(a.id), label: a.name })),
  ];

  const createAgentOptions = agents.map((a) => ({
    value: String(a.id),
    label: a.name,
  }));

  const whatsappSlotOptions = availableCatalogs.whatsapp.map((w) => ({
    value: String(w.id),
    label: `${w.label || w.slug} ${w.phone_number ? `(${w.phone_number})` : ''}`,
  }));

  const instagramSlotOptions = availableCatalogs.instagram.map((ig) => ({
    value: String(ig.id),
    label: `${ig.label || ig.slug} ${ig.ig_user_id ? `(@${ig.ig_user_id})` : ''}`,
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">
            Canais de Comunicação
          </h1>
          <p className="text-sm text-content-muted mt-1">
            Conecte números de WhatsApp, contas de Instagram ou canais Web aos seus agentes.
          </p>
        </div>
        <button
          type="button"
          onClick={handleOpenCreate}
          className="btn-primary inline-flex items-center gap-2 self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          Conectar Canal
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200">
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-content-muted" />
          <input
            type="text"
            placeholder="Buscar canais pelo nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-forge-500"
          />
        </div>
        <div className="w-full sm:w-64">
          <Select
            id="agentFilter"
            value={agentFilter}
            onChange={(e) => {
              setAgentFilter(e.target.value);
              setSearchParams(e.target.value ? { agentId: e.target.value } : {});
            }}
            options={agentOptions}
          />
        </div>
      </div>

      {/* Channel list */}
      {loading ? (
        <div className="surface-card p-12 text-center text-content-muted">
          Carregando canais...
        </div>
      ) : filteredChannels.length === 0 ? (
        <EmptyState
          icon={Radio}
          title="Nenhum canal conectado"
          description={
            search || agentFilter
              ? 'Nenhum canal encontrado com os filtros atuais.'
              : 'Conecte seu primeiro canal de WhatsApp, Instagram ou Webchat ao seu agente.'
          }
          actionLabel="Conectar Canal"
          onAction={handleOpenCreate}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredChannels.map((channel) => {
            const isWa = channel.type === 'whatsapp';
            const isIg = channel.type === 'instagram';
            const isWeb = channel.type === 'web';
            const isActive = channel.status === 'active';

            return (
              <div
                key={channel.id}
                className="surface-card p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between hover:border-slate-300 transition-colors"
              >
                <div>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className={`p-2 rounded-lg ${
                          isWa
                            ? 'bg-emerald-50 text-emerald-700'
                            : isIg
                            ? 'bg-fuchsia-50 text-fuchsia-700'
                            : 'bg-blue-50 text-blue-700'
                        }`}
                      >
                        {isWa && <MessageCircle className="h-5 w-5" />}
                        {isIg && <Camera className="h-5 w-5" />}
                        {isWeb && <Globe className="h-5 w-5" />}
                      </div>
                      <div>
                        <h3 className="font-semibold text-content-primary text-base">
                          {channel.name}
                        </h3>
                        <span className="text-xs font-medium uppercase tracking-wider text-content-muted">
                          {isWa ? 'WhatsApp' : isIg ? 'Instagram' : 'Web / PWA'}
                        </span>
                      </div>
                    </div>

                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        isActive
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}
                    >
                      {isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>

                  <div className="space-y-1.5 text-xs text-content-secondary mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <span className="text-content-muted">Agente:</span>
                      <span className="font-medium text-content-primary">
                        {channel.agent?.name || '—'}
                      </span>
                    </div>

                    {isWa && (
                      <div className="flex items-center justify-between">
                        <span className="text-content-muted">Telefone:</span>
                        <span className="font-mono text-content-primary">
                          {channel.phone_number || channel.catalog_label || '—'}
                        </span>
                      </div>
                    )}

                    {isIg && (
                      <div className="flex items-center justify-between">
                        <span className="text-content-muted">Conta:</span>
                        <span className="font-mono text-content-primary">
                          {channel.ig_user_id ? `@${channel.ig_user_id}` : channel.catalog_label || '—'}
                        </span>
                      </div>
                    )}

                    {isWeb && (
                      <div className="flex items-center justify-between">
                        <span className="text-content-muted">Canal:</span>
                        <span className="text-content-primary">Webchat / PWA</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => handleOpenEdit(channel)}
                    className="inline-flex items-center gap-1 text-xs text-content-secondary hover:text-content-primary p-1.5 rounded hover:bg-slate-100 transition-colors"
                    title="Editar nome"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Renomear
                  </button>

                  {isActive && (
                    <button
                      type="button"
                      onClick={() => setDeactivatingChannel(channel)}
                      className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 p-1.5 rounded hover:bg-red-50 transition-colors"
                      title="Desativar canal"
                    >
                      <CircleOff className="h-3.5 w-3.5" />
                      Desativar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Conectar Canal */}
      <Modal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Conectar Novo Canal"
        disableClose={createSaving}
      >
        <form onSubmit={handleCreateSubmit} className="space-y-4 my-4">
          {createError && (
            <div className="p-3 rounded bg-red-50 text-red-700 text-xs border border-red-200">
              {createError}
            </div>
          )}

          <div>
            <label htmlFor="channelAgent" className="block text-sm font-medium text-content-primary mb-1">
              Agente Vinculado
            </label>
            <Select
              id="channelAgent"
              value={createForm.agentId}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, agentId: e.target.value }))}
              options={createAgentOptions}
              required
            />
          </div>

          <Input
            id="channelName"
            label="Nome do Canal"
            placeholder="Ex: WhatsApp Atendimento, Instagram Vendas"
            value={createForm.name}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
            required
          />

          <div>
            <label htmlFor="channelType" className="block text-sm font-medium text-content-primary mb-1">
              Tipo de Canal
            </label>
            <Select
              id="channelType"
              value={createForm.type}
              onChange={(e) => {
                const newType = e.target.value;
                setCreateForm((prev) => ({
                  ...prev,
                  type: newType,
                  catalogId:
                    newType === 'whatsapp'
                      ? availableCatalogs.whatsapp[0]?.id || ''
                      : newType === 'instagram'
                      ? availableCatalogs.instagram[0]?.id || ''
                      : '',
                }));
              }}
              options={CHANNEL_TYPES}
            />
          </div>

          {/* WhatsApp slot selection */}
          {createForm.type === 'whatsapp' && (
            <div>
              {availableCatalogs.whatsapp.length === 0 ? (
                <div className="p-3 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 text-xs">
                  <p className="font-semibold mb-1">Nenhum número de WhatsApp disponível</p>
                  <p>
                    Sua conta ainda não possui slots de WhatsApp atribuídos. Entre em contato
                    com a equipe de suporte ou administrador para liberar um número para sua conta.
                  </p>
                </div>
              ) : (
                <div>
                  <label htmlFor="catalogId" className="block text-sm font-medium text-content-primary mb-1">
                    Número de WhatsApp
                  </label>
                  <Select
                    id="catalogId"
                    value={createForm.catalogId}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, catalogId: e.target.value }))}
                    options={whatsappSlotOptions}
                    required
                  />
                  <p className="text-[11px] text-content-muted mt-1">
                    Selecione o número de WhatsApp pré-configurado atribuído à sua conta.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Instagram slot selection */}
          {createForm.type === 'instagram' && (
            <div>
              {availableCatalogs.instagram.length === 0 ? (
                <div className="p-3 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 text-xs">
                  <p className="font-semibold mb-1">Nenhuma conta de Instagram disponível</p>
                  <p>
                    Sua conta ainda não possui slots de Instagram atribuídos. Entre em contato
                    com a equipe de suporte ou administrador para liberar uma conta para seu workspace.
                  </p>
                </div>
              ) : (
                <div>
                  <label htmlFor="catalogIdIg" className="block text-sm font-medium text-content-primary mb-1">
                    Conta de Instagram
                  </label>
                  <Select
                    id="catalogIdIg"
                    value={createForm.catalogId}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, catalogId: e.target.value }))}
                    options={instagramSlotOptions}
                    required
                  />
                  <p className="text-[11px] text-content-muted mt-1">
                    Selecione a conta de Instagram pré-configurada atribuída à sua conta.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Webchat delivery */}
          {createForm.type === 'web' && (
            <div className="space-y-3">
              <Input
                id="deliveryUrl"
                label="Delivery URL (opcional)"
                placeholder="https://meu-app.com/api/delivery"
                value={createForm.deliveryUrl}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, deliveryUrl: e.target.value }))}
              />
              <Input
                id="deliveryToken"
                label="Delivery Token (opcional)"
                type="password"
                placeholder="Token de autenticação para envio de mensagens"
                value={createForm.deliveryToken}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, deliveryToken: e.target.value }))}
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setCreateModalOpen(false)}
              disabled={createSaving}
              className="btn-secondary text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={
                createSaving ||
                (createForm.type === 'whatsapp' && availableCatalogs.whatsapp.length === 0) ||
                (createForm.type === 'instagram' && availableCatalogs.instagram.length === 0)
              }
              className="btn-primary text-sm"
            >
              {createSaving ? 'Conectando...' : 'Conectar Canal'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal Editar Nome */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Editar Canal"
        disableClose={editSaving}
      >
        <form onSubmit={handleEditSubmit} className="space-y-4 my-4">
          {editError && (
            <div className="p-3 rounded bg-red-50 text-red-700 text-xs border border-red-200">
              {editError}
            </div>
          )}

          <Input
            id="editChannelName"
            label="Nome do Canal"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            required
          />

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setEditModalOpen(false)}
              disabled={editSaving}
              className="btn-secondary text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={editSaving}
              className="btn-primary text-sm"
            >
              {editSaving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Confirmação de desativação */}
      <ConfirmModal
        open={Boolean(deactivatingChannel)}
        onClose={() => setDeactivatingChannel(null)}
        onConfirm={handleConfirmDeactivate}
        title="Desativar Canal"
        message={`Deseja realmente desativar o canal "${deactivatingChannel?.name}"? Mensagens recebidas por ele não serão mais processadas.`}
        confirmLabel={deactivating ? 'Desativando...' : 'Sim, desativar'}
        danger
        disableClose={deactivating}
      />
    </div>
  );
}
